import { useState, useRef, useEffect, useMemo } from 'react';
import { SelectProfesionesProps } from '@/types';
import { InputCss } from '@/components/styles';
import { Input } from '@/components/ui';

const SelectProfesiones: React.FC<SelectProfesionesProps> = ({
    nombresProfesiones = [],
    selectedProfesiones = [],
    onSelectionChange,
    onSelect,
    label,
    placeholder = 'Elige profesión',
    id,
    name = '',
    className,
    readOnly = false,

}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(selectedProfesiones[0] || '');
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSelected(selectedProfesiones[0] || '');
    }, [selectedProfesiones]);

    // Opciones filtradas según la búsqueda
    const filteredOptions = useMemo(() => {
        return nombresProfesiones
            .filter((prof) => prof.toLowerCase().includes(query.toLowerCase()))
            .sort((a, b) => (a ?? '').localeCompare(b ?? ''))
            .map((prof) => ({ value: prof, label: prof }));
    }, [query, nombresProfesiones]);

    // Cierra el menú al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (value: string) => {
        setSelected(value);
        setIsOpen(false);
        setQuery('');
        onSelectionChange?.([value]);
        onSelect?.([value]);
    };

    return (
        <div className={"relative w-full min-w-[230px]"} ref={containerRef}>
            {label && (
                <label htmlFor={id} className="block text-sm font-medium text-black pb-1">
                    <strong>{label}</strong>
                </label>
            )}

            {/* Selector */}
            <button
                type="button"
                disabled={readOnly}
                className={`w-full h-8 px-3 rounded-md border flex items-center justify-between min-w-0 ${className} ${InputCss.inputEdita}`}
                onClick={() => !readOnly && setIsOpen(!isOpen)}
            >
                <span className="truncate block w-full min-w-0">
                    {selected || placeholder}
                </span>

                <svg
                    className={`h-5 w-5 text-zinc-600 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <polyline points="18 15 12 9 6 15" />
                </svg>
            </button>

            {/* Opciones desplegables con buscador */}
            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-zinc-600 rounded-md shadow-lg max-h-60 overflow-auto">
                    <Input
                        type="text"
                        placeholder="Buscar..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        autoFocus
                    />
                    <ul>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <li
                                    key={option.value}
                                    className={`px-3 py-2 cursor-pointer hover:bg-zinc-200 ${option.value === selected ? 'bg-zinc-100 font-semibold' : ''}`}
                                    onClick={() => handleSelect(option.value)}
                                >
                                    {option.label}
                                </li>
                            ))
                        ) : (
                            <li className="px-3 py-2 text-zinc-500">No se encontraron resultados</li>
                        )}
                    </ul>
                </div>
            )}

            {/* Input oculto para formularios */}
            <input type="hidden" name={name} value={selected} />
        </div>
    );
};

export default SelectProfesiones;